import { AbstractControl, Validators } from '@angular/forms';
import { phoneNumberUtil, PhoneValidationErrors, phoneValidNumberValidator } from './phone.validator';

export type EmailOrPhoneValidationErrors =
    | { invalidEmail: true }
    | { invalidEmailOrPhone: true }
    | PhoneValidationErrors;

/**
 * Angular form validator for fields that accept either an email address or a phone number.
 *
 * Validation logic:
 * - Valid email → null
 * - Contains '@' but invalid email → `{ invalidEmail }`
 * - Valid phone (E.164 format, verified via google-libphonenumber) → null
 * - Parseable phone but invalid → specific `PhoneValidationErrors` (e.g. `{ phoneTooShort }`, `{ invalidPhoneCountryCode }`, `{ invalidPhoneNumber }`)
 * - Unparseable input (neither email nor recognisable phone) → `{ invalidEmailOrPhone }`
 *
 * @see PhoneValidationErrors
 * @see EmailOrPhoneValidationErrors
 */

export function emailOrPhoneValidator(control: AbstractControl): null | EmailOrPhoneValidationErrors {
    if (Validators.email(control) === null) return null;

    const value: string = control.value ?? '';
    if (value.includes('@')) return { invalidEmail: true };

    const phoneError = phoneValidNumberValidator(control);
    if (phoneError === null) return null;

    if (!('invalidPhoneNumber' in phoneError)) return phoneError;

    // phoneValidNumberValidator returns { invalidPhoneNumber } for both a failed parse and a
    // successful parse that fails isValidNumber. Re-parse here to distinguish the two cases:
    // if parse throws, the input is ambiguous (not recognisably a phone) → generic error.
    try {
        phoneNumberUtil.parseAndKeepRawInput(value);
        return { invalidPhoneNumber: true };
    } catch {
        return { invalidEmailOrPhone: true };
    }
}
